import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import convert from "convert-units";
import { Vector2, Vector3 } from "three";
import { useThree } from "@react-three/fiber";

import {
  setRoomSelectors,
  setLineBreakState,
  setUpperPoint,
  setLowerPoint,
  setSnapPoint,
  setLinePlacementMode,
  setLeftPosState,
  setRightPosState,
} from "../features/drawing/drwingSlice.js";
import {
  uniqueId,
  calculateAlignedPoint,
  replaceValue,
} from "../utils/uniqueId";
import { snapToPoint, getSnappedPoint } from "../utils/snapping.js";
import { getLineIntersection } from "../utils/intersect.js";
import { INITIAL_BREADTH, INITIAL_HEIGHT } from "../constant/constant.js";
import { findLineForPoint } from "../utils/coolinear.js";
import { calculateUpperAndLowerPoints } from "../utils/upperlowerpoint.js";
import {
  setPoints,
  setStoreLines,
  setStoreBoxes,
  setSelectionMode,
  setSelectedLinesState,
  setExpandRoomNamePopup,
  setRoomDetails,
  setRoomEditingMode,
  setRoomName,
  setActiveRoomButton,
  setActiveRoomIndex,
  setShowSetScalePopup,
  setFirstLinePoints,
  setHelpVideo,
  setUserStatus,
  setHighlightMenu,
  setHighlightPoint,
  setImageOpacity,
  setScaleLine,
  setScaleSetState,
} from "../Actions/ApplicationStateAction.js";
import {
  setContextualMenuStatus,
  setCurrentMousePosition,
  setCurrentStrightMousePosition,
  setDistance,
  setDragMode,
  setEnablePolygonSelection,
  setMergeLine,
  setNewLine,
  setRedoStack,
  setRoomRedoStack,
  setRoomUndoStack,
  setShowPopup,
  setShowSnapLine,
  setSnappingPoint,
  setStop,
  setTypeId,
  setUndoStack,
  updateTemoraryPolygon,
} from "../Actions/DrawingActions.js";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "react-hot-toast";
import useModes from "./useModes.js";
import usePoints from "./usePoints.js";
import { dividePolygon } from "../utils/polygon.js";
import { resetShowFirstTimePopup, setShowFirstTimePopup, setShowTutorialPopup } from "../Actions/PopupAction.js";
import SetScalePopup from "../component/Popups/SetScalePopup.js";

const MySwal = withReactContent(Swal);

export const useDrawing = () => {
  const dispatch = useDispatch();
  const {
    perpendicularLine,
    measured,
    roomSelectors,
    lineBreak,
    merge,
    snapActive,
    userLength,
    userWidth,
    snapPoint,
    linePlacementMode,
  } = useSelector((state) => state.drawing);
  const {
    typeId,
    actionHistory,
    roomActionHistory,
    stop,
    newLine,
    showSnapLine,
    snappingPoint,
    dragMode,
    mergeLine,
    shiftPressed,
    temporaryPolygon,
    enablePolygonSelection
  } = useSelector((state) => state.Drawing);
  const {
    storeLines,
    points,
    factor,
    storeBoxes,
    selectionMode,
    selectedLines,
    expandRoomPopup,
    roomEditingMode,
    activeRoomIndex,
    designStep,
    activeRoomButton,
    img,
    tutorialActive,
    autoDraw,
    novaPoints,
    highlightPoint,
    ScaleSetState,
    scaleLineId, // Ensure this is defined in the Redux store
  } = useSelector((state) => state.ApplicationState);
  const { showFirstTimePopup, firstTimePopupNumber, enableFirstTimePopup, tutorialImageFixedHeight, tutorialImageFixedWidth } = useSelector((state) => state.PopupState);

  const { toggleSelectionSplitMode, toggleSelectionMode } = useModes();
  const { screenToNDC, isPointInsidePolygon, arePointsSimilar } = usePoints();

  const [breakPoint, setBreakPoint] = useState([]);
  const [dimensions, setDimensions] = useState({ l: 50, w: 10, h: 50 });
  const [selectId, setId] = useState(null);

  // Log all line IDs whenever storeLines changes
  // useEffect(() => {
  //   if (storeLines.length > 0) {
  //     console.log("All Line IDs:", storeLines.map(line => line.id));
  //   } else {
  //     console.log("No lines present.");
  //   }
  // }, [storeLines]);

  // useEffect(() => {
  //   if (scaleLineId) {
  //     console.log("Updated scaleLineId from Redux: ", scaleLineId);
  //     console.log(scaleLineId);
  //   }
  // }, [scaleLineId]);

  const setSelectedLines = (data) => {
    dispatch(setSelectedLinesState(data));
  };

  const determineDirection = (startPoint, endPoint) => {
    var dx = endPoint.x - startPoint.x;
    var dy = endPoint.y - startPoint.y;
    let direction = null;
  
    /*
      Line directions and menu placement:
      1. Left ➝ Right  (dx > 0)  → menu at "top"
      2. Right ➝ Left  (dx < 0)  → menu at "bottom"
      3. Top ➝ Bottom  (dy < 0)  → menu at "right"
      4. Bottom ➝ Top  (dy > 0)  → menu at "left"
    */
  
    if (Math.abs(dx) > Math.abs(dy)) {
      // Dominantly horizontal
      direction = dx > 0 ? "top" : "bottom";
    } else {
      // Dominantly vertical
      direction = dy < 0 ? "right" : "left";
    }
  
    return { direction, dx: Math.abs(dx), dy: Math.abs(dy) };
  };
  
  const addPoint = (newPoint, startPoint, fact = factor, length = userLength, width = userWidth) => {
    /*
    *******************
    IMPORTANT NOTE:
    The fact[0] is sometimes coming as 0, dont know why, the value of fact[0] is unitLength from scale data, somehow the unitLength is getting as 0.
    So for a patch if fact[0] is coming as 0 its taken as 1, This fix is only as a patch.
    if fact[0] is 0 then the length of the line is becoming 0, leads to removal of line.
    */
    console.log("addPoint", newPoint, startPoint, fact, length, width)
    if(storeLines.length===0 && length === 0 && width === 0 && img && !tutorialActive){
      //toggleSelectionMode()
      //dispatch(setShowSetScalePopup(true))
      //dispatch(setContextualMenuStatus(false))
      dispatch(setFirstLinePoints([newPoint, startPoint]));
      const tempLine = {
        id: uniqueId(),
        points: [startPoint, newPoint],
        length: convert(startPoint.distanceTo(newPoint) * (fact[0] === 0 ? 1 : fact[0]))
        .from(measured)
        .to("mm"),
      // width: (1/factor[0]) * userWidth,
      width: convert(INITIAL_BREADTH / factor[1])
                            .from(measured)
                            .to("mm"),
                          height: convert(INITIAL_HEIGHT / factor[2])
                            .from(measured)
                            .to("mm"),
        widthchangetype: "between",
        widthchange: 0,
        type: "wall",
        typeId: 1,
        locked: false,
        isCustomised: null,
        isScaleLine: true,
      };
      console.log(INITIAL_BREADTH, INITIAL_HEIGHT);
      dispatch(setStoreLines([tempLine]));
      //dispatch(setImageOpacity(1));
      dispatch(setScaleLine(tempLine.id));
      return;
    }

    let previousLines = [...storeLines];
    console.log("previousLines", previousLines)
    const previousPoints = [...points];
    const previousBoxes = [...storeBoxes];
    let type =
      typeId === 1
        ? "wall"
        : typeId === 2
        ? "door"
        : typeId === 3
        ? "window"
        : typeId === 4
        ? "railing"
        : typeId === 5
        ? "imaginary"
        : typeId === 7
        ? "sliding_window"
        : "";

    let newLine = {
      id: uniqueId(),
      points: [startPoint, newPoint],
      length: convert(startPoint.distanceTo(newPoint) * (fact[0] === 0 ? 1 : fact[0]))
        .from(measured)
        .to("mm"),
      // width: (1/factor[0]) * userWidth,
      width: convert(INITIAL_BREADTH / fact[1]).from(measured).to("mm"),
height: convert(INITIAL_HEIGHT / fact[2]).from(measured).to("mm"),
      widthchangetype: "between",
      widthchange: 0,
      type,
      typeId: typeId,
      locked: false,
      isCustomised: null,
    };

    console.log("New Line: ", newLine)
    window.GAEvent("DrawTool","AddPoint", "LineCreated",type)
    let updatedStoreLines = [...storeLines];
    let intersections = [];
    let newPoints = [...points];

    // Collect all intersections
    storeLines.forEach((line) => {
      const intersection = getLineIntersection(
        line.points[0],
        line.points[1],
        startPoint,
        newPoint,
        snapActive
      );
      if (intersection) {
        intersections.push({ line, intersection });
      }
    });

    // Sort intersections based on their distance from startPoint along the new line
    intersections.sort(
      (a, b) =>
        startPoint.distanceTo(a.intersection) -
        startPoint.distanceTo(b.intersection)
    );

    let currentStartPoint = startPoint;

    // Store new segments of the new line
    intersections.forEach(({ intersection }) => {
      const splitNewLine = {
        ...newLine,
        id: uniqueId(),
        points: [currentStartPoint, intersection],
      };
      splitNewLine.length = convert(
        splitNewLine.points[0].distanceTo(splitNewLine.points[1]) * (fact[0] === 0 ? 1 : fact[0])
      )
        .from(measured)
        .to("mm");

      console.log("New line appended in updatedStoreLines with splitNewLine", splitNewLine)
      updatedStoreLines.push(splitNewLine);

      // Update the currentStartPoint for the next segment
      currentStartPoint = intersection;
      newPoints.push(currentStartPoint);
    });

    // Add the final segment of the new line
    if (!arePointsSimilar(currentStartPoint, newPoint)) {
      const finalNewLineSegment = {
        ...newLine,
        id: uniqueId(),
        points: [currentStartPoint, newPoint],
      };
      finalNewLineSegment.length = convert(
        finalNewLineSegment.points[0].distanceTo(
          finalNewLineSegment.points[1]
        ) * (fact[0] === 0 ? 1 : fact[0])
      )
        .from(measured)
        .to("mm");

      console.log("New line appended in updatedStoreLines with finalNewLineSegment", finalNewLineSegment)
      updatedStoreLines.push(finalNewLineSegment);
      newPoints.push(newPoint);
    }

    // Also handle splitting the existing lines at the intersection points
    intersections.forEach(({ line, intersection }) => {
      const splitLine1 = {
        ...line,
        id: uniqueId(),
        points: [line.points[0], intersection],
      };
      const splitLine2 = {
        ...line,
        id: uniqueId(),
        points: [intersection, line.points[1]],
      };

      // Calculate lengths for new segments
      splitLine1.length = convert(
        splitLine1.points[0].distanceTo(splitLine1.points[1]) * (fact[0] === 0 ? 1 : fact[0])
      )
        .from(measured)
        .to("mm");
      splitLine2.length = convert(
        splitLine2.points[0].distanceTo(splitLine2.points[1]) * (fact[0] === 0 ? 1 : fact[0])
      )
        .from(measured)
        .to("mm");

        const isLine1SameAsNew = (
          (splitLine1.points[0].equals(startPoint) && splitLine1.points[1].equals(newPoint)) ||
          (splitLine1.points[0].equals(newPoint) && splitLine1.points[1].equals(startPoint))
        );

        const isLine2SameAsNew = (
          (splitLine2.points[0].equals(startPoint) && splitLine2.points[1].equals(newPoint)) ||
          (splitLine2.points[0].equals(newPoint) && splitLine2.points[1].equals(startPoint))
        );

          // Replace the old line with only the segments that are NOT identical to the new line
  const lineIndex = updatedStoreLines.findIndex((l) => l.id === line.id);
  const segmentsToAdd = [];

  if (!isLine1SameAsNew && splitLine1.length > 0) {
    segmentsToAdd.push(splitLine1);
  }
  if (!isLine2SameAsNew && splitLine2.length > 0) {
    segmentsToAdd.push(splitLine2);
  }
      updatedStoreLines.splice(lineIndex, 1, ...segmentsToAdd);
      // // Replace the old line with the new segments
      // const lineIndex = updatedStoreLines.findIndex((l) => l.id === line.id);
      // updatedStoreLines.splice(lineIndex, 1, splitLine1, splitLine2);

      // Insert the intersection point into the points array
      const startIdx = newPoints.findIndex((point) =>
        point.equals(line.points[0])
      );
      const endIdx = newPoints.findIndex((point) =>
        point.equals(line.points[1])
      );
      if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
        newPoints.splice(startIdx + 1, 0, intersection);
      }
    });


    dispatch(setPoints(newPoints));
    dispatch(setImageOpacity(0.4));
    console.log("Before final lines: ", updatedStoreLines)
    const finalLines = updatedStoreLines.filter((line) => {
      return line.length !== 0;
    });
    console.log("finalLines: ", finalLines)


    dispatch(setStoreLines(finalLines));

    if (!tutorialActive) { 
      const history = [...actionHistory];
      history.push({
        type: "addPoint",
        previousLines,
        previousPoints,
        previousBoxes,
        newLines: updatedStoreLines,
        newPoints,
        newBoxes: [...storeBoxes],
      });
      dispatch(setUndoStack(history));
      dispatch(setRedoStack([]));
    }
  };

  // Function to compare two Vector3 objects
  function arePointsEqual(pointA, pointB) {
    return (
      pointA?.x === pointB?.x &&
      pointA?.y === pointB?.y &&
      pointA?.z === pointB?.z
    );
  }

  // Function to check if deleteLine points match any points in a box
  function shouldRemoveBox(boxPoints, deleteLinePoints) {
    return boxPoints.some((boxPoint) =>
      deleteLinePoints.some((deletePoint) =>
        arePointsEqual(boxPoint, deletePoint)
      )
    );
  }

  // useEffect(() => {
  //   const { pointUpper, pointLower } = calculateUpperAndLowerPoints(
  //     storeLines,
  //     factor,
  //     measured
  //   );
  //   // console.log("hello this is working");
  //   // console.log(pointUpper);

  //   // Dispatch the calculated points to the store
  //   dispatch(setUpperPoint(pointUpper));
  //   dispatch(setLowerPoint(pointLower));
  // }, [storeLines, factor, measured, dispatch]);

  const deleteSelectedRoom = () => {
    if (activeRoomIndex !== -1) {
      const rooms = [...roomSelectors];
      rooms.splice(activeRoomIndex, 1);
      dispatch(setRoomSelectors(rooms));
      setSelectedLines([]);
      const history = [...roomActionHistory];
      history.push({
        type: "updateRoom",
        oldRooms: [...roomSelectors],
        newRooms: rooms,
      });
      dispatch(setRoomUndoStack(history));
      dispatch(setRoomRedoStack([]));
      dispatch(setExpandRoomNamePopup(false));
      dispatch(setRoomDetails(""));
      dispatch(setRoomName(""));
      dispatch(setRoomEditingMode(false));
      dispatch(setActiveRoomButton(""));
      dispatch(setActiveRoomIndex(-1));
    }
  };

  const deleteSelectedLines = () => {
  let lockedCount = 0;
  let scaleLineDeleted = false;
  let scaleLineProperties = null;

  // Check if any of the selected lines is the scale line
  selectedLines.forEach(lineId => {
    const line = storeLines.find(l => l.id === lineId);
    if (line && line.typeId === 6 && line.type === "firstLine") {
      scaleLineDeleted = true;
      scaleLineProperties = {
        points: [...line.points],
        length: line.length,
        isScaleLine: true,
        type: "firstLine",
        typeId: 6
      };
    }
  });

    const roomupdate = roomSelectors
      .map((room) => {
        const updatedWallIds = room.wallIds.filter((lineId) => {
          const line = storeLines.find((line) => line.id === lineId);
          if (selectedLines.includes(lineId) && line?.locked) {
            return true;
          }
          return !selectedLines.includes(lineId);
        });

        if (updatedWallIds.length === 1) {
          return null;
        }

        return {
          ...room,
          wallIds: updatedWallIds,
        };
      })
      .filter((room) => room !== null);

    dispatch(setRoomSelectors(roomupdate));
    const deletedLines = [];
    const updatedLines = storeLines.filter((line) => {
      if (selectedLines.includes(line.id)) {
        if (line.locked) {
          lockedCount += 1;
          return true;
        }
        deletedLines.push(line);
        return false;
      }
      return true;
    });
    const deletedPoints = [];
    let boxes = new Set();
    deletedLines.forEach((line) => {
      const deleteLinePoints = line.points;
      const deletedBoxes = storeBoxes.filter((box) =>
        shouldRemoveBox([box.p1, box.p2, box.p3, box.p4], deleteLinePoints)
      );
      deletedBoxes.forEach((box) => boxes.add(box));
      deletedPoints.push(line.points[0], line.points[1]);
    });
    dispatch(setStoreBoxes(storeBoxes.filter((box) => !boxes.has(box))));

    const history = [...actionHistory];
    history.push({
      type: "delete",
      deletedLines,
      deletedPoints,
      deletedBoxes: Array.from(boxes),
    });
    dispatch(setUndoStack(history));
    dispatch(setRedoStack([]));

    const pointsToKeep = [];

    if (updatedLines.length > 0) {
      updatedLines.forEach((line) => {
        pointsToKeep.push(line.points[0], line.points[1]);
      });

      const updatedPoints = points.filter((point) =>
        pointsToKeep.some((p) => p.equals(point))
      );

      if (scaleLineDeleted && scaleLineProperties) {
        const newScaleLine = updatedLines.find(line => 
          line.typeId === 1 && !line.locked
        );

        if (newScaleLine) {
          const updatedLinesWithNewScale = updatedLines.map(line => {
            if (line.id === newScaleLine.id) {
              return {
                ...line,
                type: "firstLine",
                typeId: 6,
                isScaleLine: true
              };
            }
            return line;
          });

          dispatch(setStoreLines(updatedLinesWithNewScale));
          dispatch(setScaleLine(newScaleLine.id));
          console.log(`New scale line assigned: ${newScaleLine.id}`);
        } else {
          dispatch(setStoreLines(updatedLines));
          dispatch(setScaleLine(null));
        }
      } else {
        // No suitable line found to become the new scale line
        dispatch(setStoreLines(updatedLines));
      }

      dispatch(setPoints(updatedPoints));
    } else {
      dispatch(setStoreLines([]));
      dispatch(setPoints([]));
      dispatch(setScaleLine(null));
    }
    
    setSelectedLines([]);
    dispatch(setContextualMenuStatus(false));

    if (lockedCount > 1) {
      toast(`${lockedCount} structures were locked and were not deleted.`, {
        icon: "⚠️",
        style: {
          fontFamily: "'DM Sans', sans-serif",
          color: "#000",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
        },
      });
    } else if (lockedCount === 1) {
      toast(`${lockedCount} structure was locked and was not deleted.`, {
        icon: "⚠️",
        style: {
          color: "#000",
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
        },
      });
    }
  };

  const addRoom = (roomName, roomType) => {
    const room = {
      roomId: uniqueId(),
      polygon: temporaryPolygon,
      roomName: roomName,
      roomType: roomType,
      wallIds: [...selectedLines],
    };
    let history = [...roomActionHistory];
    history = history.filter(
      (action) => action.type !== "addPoint"
    ); // clearing temporary polygon's points from undo stack
    history.push({
      type: "updateRoom",
      oldRooms: [...roomSelectors],
      newRooms: [...roomSelectors, room],
    });
    dispatch(setRoomUndoStack(history));
    dispatch(setRoomRedoStack([]));
    dispatch(setRoomSelectors([...roomSelectors, room]));
    setSelectedLines([]);
    dispatch(updateTemoraryPolygon([]))
  };

  const handleClick = (event) => {
      console.log("HandleClick")
    
  
    if(!tutorialActive && showFirstTimePopup && (firstTimePopupNumber === 4 || firstTimePopupNumber === 5)) {
      dispatch(resetShowFirstTimePopup());
    }
    dispatch(setHelpVideo(false))
    if (selectionMode && !enablePolygonSelection && activeRoomButton !== "divide") {
      setSelectedLines([]);
      dispatch(setContextualMenuStatus(false));
      dispatch(setShowPopup(false));
      if (designStep === 3) {
        dispatch(setExpandRoomNamePopup(false));
        dispatch(setRoomDetails(""));
        dispatch(setRoomName(""));
        dispatch(setRoomEditingMode(false));
        dispatch(setActiveRoomButton(""));
        dispatch(setActiveRoomIndex(-1));
        if(showFirstTimePopup){
          dispatch(resetShowFirstTimePopup());
          if(firstTimePopupNumber === 13){
            dispatch(setUserStatus())
          }
        }
      }
      return;
    }

    let point = screenToNDC(event.clientX, event.clientY);

    if(designStep === 3 && enablePolygonSelection && activeRoomButton !== "divide"){
      const polygon = [...temporaryPolygon];
      const history = [...roomActionHistory];
      if(showSnapLine){
        polygon.push(snappingPoint[0]);
        history.push({
          type: "addPoint",
          oldPolygon: [...temporaryPolygon],
          newPolygon: [...polygon],
        });
      }else{
        let cuuPoint = point;
        roomSelectors.forEach((room) => {
          const points = room.polygon;
          for (let i = 0; i < points.length; i++) {
            const currPoint = new Vector3(points[i].x, points[i].y, 0);
            if (point.distanceTo(currPoint) < 10) {
              cuuPoint = points[i];
              break;
            }
          }
        });
        polygon.push(cuuPoint);
        history.push({
          type: "addPoint",
          oldPolygon: [...temporaryPolygon],
          newPolygon: [...polygon],
        });
      }
      window.GAEvent("DrawTool","Polygon", "PointAdded")
      dispatch(setRoomUndoStack(history));
      dispatch(setRoomRedoStack([]));
      dispatch(updateTemoraryPolygon(polygon));
      const newLine = [...selectedLines];
      storeLines.map((line) => {
        const midpoint = new Vector3()
          .addVectors(line.points[0], line.points[1])
          .multiplyScalar(0.5);
        const tolerance = 10;
        const maxX = midpoint.x + tolerance;
        const minX = midpoint.x - tolerance;
        const maxY = midpoint.y + tolerance;
        const minY = midpoint.y - tolerance;
        if (
          isPointInsidePolygon(polygon, { x: maxX, y: midpoint.y }) ||
          isPointInsidePolygon(polygon, { x: minX, y: midpoint.y }) ||
          isPointInsidePolygon(polygon, { x: midpoint.x, y: maxY }) ||
          isPointInsidePolygon(polygon, { x: midpoint.x, y: minY })
        ) {
          newLine.push(line.id);
        }
      });
      dispatch(setSelectedLinesState(newLine));
      return
    }
    if(designStep === 3 && activeRoomButton === "divide"){
      const line = [...temporaryPolygon];
      line.push(point);
      if(line.length === 1){
        dispatch(updateTemoraryPolygon(line));
      }
      if(line.length === 2){
        const history = [...roomActionHistory];
        const polygon = roomSelectors[activeRoomIndex].polygon;
        const newPolygon = dividePolygon(polygon, line);
        if (newPolygon.length > 1) {
          window.GAEvent("DrawTool","Polygon", "PolygonDivided")
          const newRooms = roomSelectors.filter(
            (room, index) => index !== activeRoomIndex
          );
          newPolygon.forEach((polygon, index) => {
            const newLine = [];
            storeLines.map((line) => {
              const midpoint = new Vector3()
                .addVectors(line.points[0], line.points[1])
                .multiplyScalar(0.5);
              const tolerance = 10;
              const maxX = midpoint.x + tolerance;
              const minX = midpoint.x - tolerance;
              const maxY = midpoint.y + tolerance;
              const minY = midpoint.y - tolerance;
              if (isPointInsidePolygon(polygon, {x: maxX, y: midpoint.y}) || isPointInsidePolygon(polygon, {x: minX, y: midpoint.y}) || isPointInsidePolygon(polygon, {x: midpoint.x, y: maxY}) || isPointInsidePolygon(polygon, {x: midpoint.x, y: minY})) {
                newLine.push(line.id);
              }
            });
            const newRoom = {
              roomId: uniqueId(),
              polygon: polygon,
              roomName: `New Room ${roomSelectors.length + index + 1}`,
              roomType: "",
              wallIds: [...newLine],
            };
            newRooms.push(newRoom);
          });
          history.push({
            type: "updateRoom",
            oldRooms: [...roomSelectors],
            newRooms: [...newRooms],
          });
          dispatch(setRoomUndoStack(history));
          dispatch(setRoomRedoStack([]));
          dispatch(setRoomSelectors(newRooms));
          dispatch(setEnablePolygonSelection(false));
          dispatch(setActiveRoomButton(""));
          dispatch(setActiveRoomIndex(-1));
        } else {
          toast.error("Invalid Division, kindly draw to divide the room", {
            style: {
              color: "#000",
              boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
              borderRadius: "8px",
              fontFamily: "'DM Sans', sans-serif",
            },
          });
        }
        dispatch(updateTemoraryPolygon([]));
      }
      return
    }
    if (selectionMode || merge || (designStep === 1 && !tutorialActive)) return;

    if (lineBreak) {
      if (!findLineForPoint(point, storeLines, snapActive)) {
        dispatch(setLineBreakState(false));
        if (!selectionMode) {
          toggleSelectionSplitMode();
        }
      }
      return;
    }

    if (tutorialActive) {
      if(highlightPoint){
        if(Math.abs(point.x - highlightPoint[0])> 10 ||  Math.abs(point.y - highlightPoint[1]) > 10){
          return; // do not register any click if its not at the junction point (aka highlight point)
        }
      }
      const newPoints = [
        ...new Map([...points, point].map(p => [`${p.x},${p.y},${p.z}`, p])).values()
      ]
      const topLeft = [-tutorialImageFixedWidth / 2, tutorialImageFixedHeight / 2, -10];
      const topRight = [tutorialImageFixedWidth / 2, tutorialImageFixedHeight / 2, -10];
      const bottomLeft = [-tutorialImageFixedWidth / 2, -tutorialImageFixedHeight / 2, -10];
      const bottomRight = [tutorialImageFixedWidth / 2, -tutorialImageFixedHeight / 2, -10];
      const rightMiddle = [(topRight[0] + bottomRight[0]) / 2, (topRight[1] + bottomRight[1]) / 2, -10];
      const bottomMiddle = [(bottomLeft[0] + bottomRight[0]) / 2, (bottomLeft[1] + bottomRight[1]) / 2, -10];
      let pos = [topRight[0] + 300, topRight[1] - 200, -10];
      let popupNumber = 4;
      let show = true
      let highlightMenu = "wall"
      if(newPoints.length === 1){
        point = new Vector3(topLeft[0] + 10, topLeft[1] - 10, 0) // no matter where the user click always record the point at top left junction of image
        dispatch(setHighlightPoint([topRight[0] - 10, topRight[1] - 10, topRight[2]])) // set highlight point at top right junction of image
      }
      else if(newPoints.length === 2){
        pos = [(topRight[0] + bottomRight[0]) /2 - 40, (topRight[1] + bottomRight[1] ) /2 - 50, -10]
        popupNumber = 5;
        dispatch(setHighlightPoint([rightMiddle[0] - 10, rightMiddle[1] + 55, rightMiddle[2]])) // set highlight point at top windod junction of image
      }else if(newPoints.length === 3){
        show = false
        dispatch(setHighlightPoint([rightMiddle[0] - 10, rightMiddle[1] - 55, rightMiddle[2]])) // set highlight point at bottom window junction of image
      }
      else if(newPoints.length === 4){
        pos = [point?.x - 25, point?.y - 90, point?.z]
        highlightMenu = "window"
        popupNumber = 6;
        dispatch(setHighlightPoint([bottomRight[0] - 10, bottomRight[1] + 10, bottomRight[2]])) // set highlight point at bottom right junction of image
      }else if(newPoints.length === 5){
        show = false
        dispatch(setHighlightPoint([bottomMiddle[0] + 75, bottomRight[1] + 10, bottomRight[2]])) // set highlight point at right dorr junction of image
      }else if(newPoints.length === 6){
        show = false
        dispatch(setHighlightPoint([bottomMiddle[0] - 35, bottomRight[1] + 10, bottomRight[2]])) // set highlight point at left door junction of image
      }else if(newPoints.length === 7){
        pos = [point?.x + 80, point?.y + 25, point?.z]
        popupNumber = 7;
        highlightMenu = "door"
        dispatch(setHighlightPoint([topLeft[0] + 10, bottomRight[1] + 10, bottomRight[2]])) // set highlight point at bottom left junction of image
      }else if(newPoints.length === 8){
        pos = [topLeft[0] + 370, topLeft[1] - 180, -10]
        popupNumber = 8;
        dispatch(setHighlightPoint([topLeft[0] + 10, topLeft[1] - 10, 0])) // set highlight point at top left junction of image
      }else if(Math.abs(point.x - topLeft[0]) < 50 && Math.abs(point.y - topLeft[1]) < 50 && newPoints.length > 7){
        pos = [point?.x - 30 , point?.y-90, point?.z]
        popupNumber = 9;
        highlightMenu = "stopDrawing"
        dispatch(setHighlightPoint(null))
      }
      else{
        show = false
      }
      if (show) {
        dispatch(setShowTutorialPopup({
          showTutorialPopup: true,
          tutorialPopupType: "canvas",
          popupDismissable: false,
          tutorialPopupNumber: popupNumber,
          tutorialCustomisedPosition: pos
        }))
      }else{
        dispatch(setShowTutorialPopup({
          showTutorialPopup: false,
          tutorialPopupType: "",
          popupDismissable: false,
          tutorialPopupNumber: 0,
          tutorialCustomisedPosition: null
        }))
      }
      dispatch(setHighlightMenu(highlightMenu))
    }

    if (newLine) {
      dispatch(setStop(!stop));
      // if (designStep === 2) {
      //   const pointToSend = [point?.x + 40, point?.y + 100, point?.z];
      //   dispatch(setContextualMenuStatus(true, pointToSend, "neutral"));
      // }
      dispatch(setNewLine(false));

      point = snapToPoint(point, autoDraw? novaPoints.map(item => item.point):points, storeLines, snapActive, factor, measured, snapPoint, linePlacementMode);

      const newPoint = [...points, point];
      const breaking = [...breakPoint, point];
      setBreakPoint(breaking);
      dispatch(setPoints(newPoint));
      return;
    }

    // General snapping logic before final snapToPoint
    if (snapActive) {
      const lastPoint = points.length > 0 ? points[points.length - 1] : null;
      if (points.length > 0 && perpendicularLine) {
        point = calculateAlignedPoint(lastPoint, point);
      }
      const { snappedPoint } = getSnappedPoint(point, lastPoint, points, snapActive);
      point = snappedPoint;
    }

    if (!autoDraw && perpendicularLine && points.length > 0) {
      point = calculateAlignedPoint(points[points.length - 1], point);
    }
    if (!perpendicularLine && points.length > 0) {
      const lastPoint = points[points.length - 1];
      const position = calculateAlignedPoint(lastPoint, point);
      const V1 = new Vector3().subVectors(position, lastPoint);
      const V2 = new Vector3().subVectors(point, lastPoint);
      const angleInRadians = V1.angleTo(V2);
      const angleInDegrees = angleInRadians * (180 / Math.PI);
      if(angleInDegrees < 3){
        point = position;
      }
    }

    point = snapToPoint(point, autoDraw?novaPoints.map(item => item.point): points, storeLines, snapActive, factor, measured, snapPoint, linePlacementMode);

    const newPoints = [...points, point];
    dispatch(setPoints(newPoints));

    if (showSnapLine) {
      addPoint(snappingPoint[0], newPoints[newPoints.length - 2]);
    }
    console.log("point", point)

    if (points.length >= 1) {
      if (designStep === 2 || tutorialActive) {
        let pointToSend = [null, null, null];
        const direction_data = determineDirection(newPoints[newPoints.length - 2], point);
        console.log("direction_data", direction_data)
        const direction = direction_data["direction"]
        // Height of the contextal menu with the buttons is 88 px
        if(tutorialActive){
          if (direction === "right") {
            pointToSend = [point?.x + 10, point?.y + 115, point?.z];
          }
          else if (direction === "left") {
            pointToSend = [point?.x - 230, point?.y - direction_data['dy']/2+77, point?.z];
          }
          else if (direction === "top") {
            pointToSend = [point?.x - 225, point?.y + 113, point?.z];
          }
          else if (direction === "bottom") {
            pointToSend = [point?.x + 10, point?.y - 15, point?.z];
          }
        } else {
          if (direction === "right") {
            pointToSend = [point?.x + 10, point?.y + 110, point?.z];
          }
          else if (direction === "left") {
            pointToSend = [point?.x - 350, point?.y - 15, point?.z];
          }
          else if (direction === "top") {
            pointToSend = [point?.x - 350, point?.y + 112, point?.z];
          }
          else if (direction === "bottom") {
            pointToSend = [point?.x + 10, point?.y - 15, point?.z];
          }
        }

        if(!showFirstTimePopup && firstTimePopupNumber < 5 && enableFirstTimePopup && storeLines.length === 0 && !(storeLines.length === 0 && !img) && !tutorialActive){
          pointToSend = [0, 0, 0]
          dispatch(setShowFirstTimePopup({
            showFirstTimePopup: true,
            firstTimePopupNumber: 5,
            popupDismissable: true,
            firstTimePopupType: "canvas",
            customisedPosition: [pointToSend[0] - 100, pointToSend[1] + 100, pointToSend[2]],
          }))
        }else if(!showFirstTimePopup && firstTimePopupNumber < 5 && enableFirstTimePopup && storeLines.length === 1 && !img && !tutorialActive){
          pointToSend = [0, 0, 0]
          dispatch(setShowFirstTimePopup({
            showFirstTimePopup: true,
            firstTimePopupNumber: 5,
            popupDismissable: true,
            firstTimePopupType: "canvas",
            customisedPosition: [pointToSend[0] - 100, pointToSend[1] + 100, pointToSend[2]],
          }))
        }
        dispatch(setContextualMenuStatus(true, pointToSend, direction));
      }
      addPoint(point, newPoints[newPoints.length - 2]);
    }

    // if (newPoints.length === 2 && firstTime) {
    //   setFirstTime(false);
    //   const userHeight = parseFloat(
    //     prompt("Enter the height of the first line:")
    //   );
    //   const userLength = parseFloat(
    //     prompt("Enter the length of the first line:")
    //   );
    //   const userWidth = parseFloat(
    //     prompt("Enter the thickness of the first line:")
    //   );
    //   const lfactor =
    //     userLength / point.distanceTo(newPoints[newPoints.length - 2]);
    //   const wfactor = INITIAL_BREADTH / userWidth;
    //   const hfactor = INITIAL_HEIGHT / userHeight;
    //   dispatch(setFactor([lfactor, wfactor, hfactor]));

    //   let newLine = {
    //     id: uniqueId(),
    //     points: [newPoints[newPoints.length - 2], point],
    //     length: convert(
    //       newPoints[newPoints.length - 2].distanceTo(point) * lfactor
    //     )
    //       .from(measured)
    //       .to("mm"),
    //     width: convert(INITIAL_BREADTH / wfactor)
    //       .from(measured)
    //       .to("mm"),
    //     height: convert(INITIAL_HEIGHT / hfactor)
    //       .from(measured)
    //       .to("mm"),
    //     widthchangetype: "between",
    //     widthchange: 0,
    //     type: "wall",
    //   };
    //   dispatch(setStoreLines([newLine]));
    // }
  };

  const handleMergeClick = () => {
    if (selectedLines.length === 0) {
      return;
    } else {
      let newMergeLines = [];
      selectedLines.forEach((line) => {
        newMergeLines.push(line);
      });
      dispatch(setMergeLine(newMergeLines));
      if (selectedLines.length > 1) {
        handleMerge(newMergeLines);
      }
    }
  };

  const handleMerge = (storeid) => {
    let updatedLine = [...storeLines];
    let merged = false;
    let lockedCount = 0;

    // Sort storeids based on their indices in storeLines to maintain order
    storeid.sort(
      (a, b) =>
        storeLines.findIndex((line) => line.id === a) -
        storeLines.findIndex((line) => line.id === b)
    );
    const history = [...actionHistory];

    let i = 0;
    while (i < storeid.length - 1) {
      let idx1 = updatedLine.findIndex((line) => line.id === storeid[i]);
      let idx2 = updatedLine.findIndex((line) => line.id === storeid[i + 1]);

      const line1 = updatedLine[idx1];
      const line2 = updatedLine[idx2];

      // Check if either line is locked
      if (line1?.locked || line2?.locked) {
        lockedCount += line1.locked ? 1 : 0;
        lockedCount += line2.locked ? 1 : 0;
        i++;
        continue; // Skip merging if any line is locked
      }

      if (
        Math.abs(idx1 - idx2) === 1 &&
        line1.points[1].equals(line2.points[0])
      ) {
        if (
          line1.points[0].x === line2.points[1].x ||
          line1.points[0].y === line2.points[1].y
        ) {
          if (line1.typeId === 1 && line2.typeId === 1) {
            const newline = {
              ...line1,
              points: [line1.points[0], line2.points[1]],
              length: convert(
                line1.points[0].distanceTo(line2.points[1]) * factor[0]
              )
                .from(measured)
                .to("mm"),
            };

            // Replace the two merged lines with the new line
            updatedLine.splice(idx1, 2, newline);
            storeid.splice(i, 2, newline.id); // Update storeid with the new line's ID
            merged = true;
            history.push({
              type: "merge",
              originalLines: [line1, line2],
              mergedLine: newline,
            });
            i = Math.max(0, i - 1); // Re-check from the previous line for further merging
          } else {
            i++;
            toast(
              `Some lines were not wall and could not be merged with a wall.`,
              {
                icon: "⚠️",
                style: {
                  color: "#000",
                  boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
                  borderRadius: "8px",
                  fontFamily: "'DM Sans', sans-serif",
                },
              }
            );
          }
        } else {
          i++;
        }
      } else {
        i++;
      }
    }

    if (merged) {
      dispatch(setStoreLines(updatedLine));
      dispatch(setMergeLine([]));
      dispatch(setUndoStack(history));
      dispatch(setRedoStack([]));
    }
    setSelectedLines([]);
    dispatch(setContextualMenuStatus(false));

    // Show a hot-toast notification if any lines were locked
    if (lockedCount > 0) {
      toast(`Some lines were locked and not merged.`, {
        icon: "⚠️",
        style: {
          color: "#000",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
          borderRadius: "8px",
          fontFamily: "'DM Sans', sans-serif",
        },
      });
    }
    return merged;
  };

  const handleLineClick = (e, id) => {
      console.log("handleLineClick")

    // If we're in scale setting mode, show the scale popup and return early
  
  
    if(designStep === 3){
      if(expandRoomPopup){
        if(enablePolygonSelection){
          return
        }
      }else{
        return
      }
    }
    if(selectionMode){
      e.nativeEvent.stopPropagation();
    }
    window.GAEvent("DrawTool", "Canvas", "LineClicked");
    
    let storeid = [];
    if (merge) {
      if (!mergeLine.find((line) => line === id)) {
        storeid = [...mergeLine, id];
        dispatch(setMergeLine([...mergeLine, id]));
      }
    }

    if (!lineBreak && !merge && selectionMode) {
      const line = storeLines.find((line) => line.id === id);
      let pointToSend = [0, 0, 0];
      let idx = 0;
      let position = "neutral";
      if (
        Math.abs(line?.points[0]?.x - line?.points[1]?.x) <
        Math.abs(line?.points[0]?.y - line?.points[1]?.y)
      ) {
        if (line?.points[1]?.y > line?.points[0]?.y) {
          idx = 1;
        }
        pointToSend = [
          line?.points[idx]?.x - 400,
          line?.points[idx]?.y + 100,
          line?.points[idx]?.z,
        ];
        position = "right";
      } else {
        if (line?.points[0]?.x > line?.points[1]?.x) {
          idx = 1;
        }
        pointToSend = [
          line?.points[idx]?.x + 20,
          line?.points[idx]?.y - 40,
          line?.points[idx]?.z,
        ];
        position = "bottom";
      }
      let newPoint = line?.points[1]
      let point = line?.points[0]

      const direction_data = determineDirection(newPoint, point);
      console.log("direction_data", direction_data)
      const direction = direction_data["direction"]

      if (direction_data["dy"]==0){
        position = "bottom"
        pointToSend = [Math.min(line?.points[0]?.x, line?.points[1]?.x) + direction_data["dx"]/2, point?.y - 10, point?.z];
      } else {
        position = "right"
        pointToSend = [point?.x + 10, Math.min(line?.points[0]?.y, line?.points[1]?.y) + direction_data["dy"]/2, point?.z];
      }


      if(!ScaleSetState){
        dispatch(setContextualMenuStatus(true, pointToSend, position));
      } else {
        console.log("Line CLick:", id)
        dispatch(setShowFirstTimePopup({
          showFirstTimePopup: true,
          firstTimePopupNumber: 15,
          firstTimePopupType: "ui",
          // popupDismissable: true,
        }))
      }

    }

    if (lineBreak) {
      const point = screenToNDC(e.clientX, e.clientY)
      if (findLineForPoint(point, storeLines, snapActive)) {
        let { closestPointOnLine } = findLineForPoint(
          point,
          storeLines,
          snapActive
        );
        breakingLine(closestPointOnLine, id);
      } else {
        dispatch(setLineBreakState(false));
        if (!selectionMode) {
          toggleSelectionSplitMode();
        }
      }
      return;
    }

    let merged = false;
    if (merge && storeid.length >= 2) {
      merged = handleMerge(storeid);
    }

    const selectedLine = storeLines.find((line) => line.id === id);

    if (selectionMode && !merged) {
      const selected = selectedLines.includes(id)
        ? selectedLines.filter((lineId) => lineId !== id)
        : merge || expandRoomPopup || shiftPressed
        ? [...selectedLines, id]
        : [id];
      setSelectedLines(selected);
      if (designStep === 3 && roomEditingMode && activeRoomIndex >= 0) {
        let newRooms = [...roomSelectors];
        newRooms[activeRoomIndex] = {
          ...newRooms[activeRoomIndex],
          wallIds: selected,
        };
        dispatch(setRoomSelectors(newRooms));
      }
      if (!selectedLines.includes(id)) {
        dispatch(setShowPopup(true));
        // dispatch(updateLineTypeId(selectedLine.id,selectedLine.typeId || 1))
        dispatch(setTypeId(selectedLine.typeId || 1));
      } else {
        dispatch(setShowPopup(false));
        dispatch(setContextualMenuStatus(false));
        dispatch(setTypeId(1));
      }
    }
    if (ScaleSetState) {
    console.log("selectedLine", selectedLine)
    dispatch(setScaleLine(id));
    dispatch(setShowSetScalePopup(true));
    dispatch(setLeftPosState(selectedLine.points[0]));
    dispatch(setRightPosState(selectedLine.points[1]));
    dispatch(setFirstLinePoints([selectedLine.points[0], selectedLine.points[1]]));
    //dispatch(setScaleSetState(false)); // Reset the scale setting mode
    return;
  }
  };

  const breakingLine = (point, id) => {
    const idx = storeLines.findIndex((line) => line.id === id);
    const line = storeLines.filter((line) => line.id === id);
    if (line[0] && line[0]?.locked) {
      toast("Line is locked and cannot be split.", {
        icon: "⚠️",
        style: {
          fontFamily: "'DM Sans', sans-serif",
          color: "#000",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.25)",
        },
      });
      return;
    }
    if (storeLines[idx].typeId !== 1) return;
    if (idx === -1) return; // Line not found
    let updatedLine = storeLines[idx];

    let store = [...storeLines];
    let pointsVal = [...points];

    // Create the first split line
    const splitNewLine = {
      ...updatedLine,
      id: uniqueId(),
      points: [updatedLine.points[0], point],
    };
    splitNewLine.length = convert(
      splitNewLine.points[0].distanceTo(splitNewLine.points[1]) * factor[0]
    )
      .from(measured)
      .to("mm");

    // Create the second split line
    const splitNewLine1 = {
      ...updatedLine,
      id: uniqueId(),
      points: [point, updatedLine.points[1]],
    };
    splitNewLine1.length = convert(
      splitNewLine1.points[0].distanceTo(splitNewLine1.points[1]) * factor[0]
    )
      .from(measured)
      .to("mm");

    // Replace the original line with the two new split lines
    store.splice(idx, 1, splitNewLine, splitNewLine1);

    // Update the points list by inserting the break point
    const startindex = points.findIndex((p) => p.equals(updatedLine.points[0]));
    const endIndex = points.findIndex((p) => p.equals(updatedLine.points[1]));

    if (startindex !== -1 && endIndex !== -1 && startindex < endIndex) {
      pointsVal.splice(startindex + 1, 0, point);
    }

    // Dispatch the updated store and points
    dispatch(setStoreLines(store));
    dispatch(setPoints(pointsVal));
    const history = [...actionHistory];
    history.push({
      type: "split",
      originalLine: updatedLine,
      splitLines: [splitNewLine, splitNewLine1],
      newPoint: point, // store the breakpoint location
    });
    dispatch(setUndoStack(history));
    dispatch(setRedoStack([]));
  };

  return {
    addPoint,
    dimensions,
    handleClick,
    handleLineClick,
    setDimensions,
    deleteSelectedLines,
    deleteSelectedRoom,
    handleMergeClick,
    addRoom,
  };
};
